import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Col, Divider, Form, Input, InputNumber, message, Modal, Radio,
  Row, Select, Switch, Tag, Typography
} from 'antd';
import { accountsApi } from './accountsApi';
import {
  ACCOUNT_SUBTYPE_LABELS,
  isValidSubtypeForType,
  subtypeForCanonicalGroup,
  canonicalGroupForSubtype,
  subtypeOptionsForType,
  groupOptionsForType
} from './coaClassification';
import './accounts.css';

const { Text } = Typography;

// ─────────────────────────────────────────────────────────────────
// Glass-i — Ledger Account create / edit / view modal.
//
// Centered AntD Modal. Three modes:
//   - 'add'  → empty form, all fields editable, code/type/normalBalance
//              required on the server.
//   - 'edit' → pre-filled, code + type + normalBalance LOCKED (server
//              rejects them in updateSchema); other fields editable.
//   - 'view' → read-only render of the same form (no submit).
//
// Account type drives normal balance automatically:
//   asset / expense       → debit-normal
//   liability / income /
//     equity              → credit-normal
// Operator can override only on create (rare — keep the auto rule
// unless they really know better).
//
// Opening balance + opening-balance-type cross-field:
//   if openingBalance > 0 then openingBalanceType MUST be set to
//   'debit' or 'credit' (matches backend `validateLedgerCreate`).
// ─────────────────────────────────────────────────────────────────

const ACCOUNT_TYPES = [
  { value: 'asset',     label: 'Asset',     normal: 'debit'  },
  { value: 'liability', label: 'Liability', normal: 'credit' },
  { value: 'equity',    label: 'Equity',    normal: 'credit' },
  { value: 'income',    label: 'Income',    normal: 'credit' },
  { value: 'expense',   label: 'Expense',   normal: 'debit'  }
];

const ACCOUNT_TYPE_COLORS = {
  asset:     'green',
  liability: 'red',
  equity:    'gold',
  income:    'blue',
  expense:   'orange'
};

const initialForm = () => ({
  accountCode:        '',
  accountName:        '',
  accountType:        null,
  accountGroup:       '',
  accountSubtype:     null,
  normalBalance:      null,
  gstRelevant:        false,
  isControlAccount:   false,
  openingBalance:     0,
  openingBalanceType: null,
  isActive:           true
});

function unwrapApiError(err, fallback) {
  return err?.response?.data?.error?.message || err?.message || fallback;
}

export default function LedgerModal({
  open, mode = 'add', record = null, onClose, onSaved
}) {
  const [form] = Form.useForm();
  const [busy, setBusy] = useState(false);
  const isEdit = mode === 'edit';
  const isView = mode === 'view';
  const readOnly = isView;

  // Reset form whenever modal opens or the record changes.
  useEffect(() => {
    if (!open) return;
    if (mode === 'add' || !record) {
      form.resetFields();
      form.setFieldsValue(initialForm());
    } else {
      form.setFieldsValue({
        accountCode:        record.accountCode  || '',
        accountName:        record.accountName  || '',
        accountType:        record.accountType  || null,
        accountGroup:       record.accountGroup || '',
        accountSubtype:     record.accountSubtype || null,
        normalBalance:      record.normalBalance || null,
        gstRelevant:        record.gstRelevant === true,
        isControlAccount:   record.isControlAccount === true,
        openingBalance:     Number.isFinite(record.openingBalance) ? record.openingBalance : 0,
        openingBalanceType: record.openingBalanceType || null,
        isActive:           record.isActive !== false
      });
    }
  }, [open, mode, record, form]);

  // Stage 6B.9B — re-render the type-filtered Group + Subtype option
  // lists when the operator changes account_type.
  const accountTypeWatch = Form.useWatch('accountType', form);
  const groupOptions   = useMemo(
    () => groupOptionsForType(accountTypeWatch), [accountTypeWatch]);
  const subtypeOptions = useMemo(
    () => subtypeOptionsForType(accountTypeWatch), [accountTypeWatch]);

  // When accountType changes (only on create), auto-set normalBalance
  // and clear stale group/subtype if they're no longer valid for the
  // new type.
  const onTypeChange = useCallback((value) => {
    const t = ACCOUNT_TYPES.find((x) => x.value === value);
    const patch = {};
    if (t) patch.normalBalance = t.normal;
    // Clear subtype that no longer belongs to the new type.
    const curSubtype = form.getFieldValue('accountSubtype');
    if (curSubtype && !isValidSubtypeForType(value, curSubtype)) {
      patch.accountSubtype = null;
    }
    form.setFieldsValue(patch);
  }, [form]);

  // Stage 6B.9B — when operator picks a canonical group, auto-fill
  // the matching subtype (only if the subtype field is empty — never
  // override a manual subtype the operator already picked).
  const onGroupChange = useCallback((value) => {
    const inferred = subtypeForCanonicalGroup(accountTypeWatch, value);
    const curSubtype = form.getFieldValue('accountSubtype');
    if (inferred && !curSubtype) {
      form.setFieldsValue({ accountSubtype: inferred });
    }
  }, [accountTypeWatch, form]);

  // Stage 6B.9B — when operator picks a subtype, auto-fill the
  // matching canonical group (only if group is empty — never override
  // a custom group string).
  const onSubtypeChange = useCallback((value) => {
    const inferredGroup = canonicalGroupForSubtype(accountTypeWatch, value);
    const curGroup = form.getFieldValue('accountGroup');
    if (inferredGroup && !curGroup) {
      form.setFieldsValue({ accountGroup: inferredGroup });
    }
  }, [accountTypeWatch, form]);

  const onSubmit = useCallback(async () => {
    if (readOnly) return onClose?.();
    let values;
    try {
      values = await form.validateFields();
    } catch (_e) {
      return;
    }

    // Cross-field: opening balance > 0 requires a side.
    const ob = Number(values.openingBalance) || 0;
    if (ob > 0 && !values.openingBalanceType) {
      message.warning('Choose Debit or Credit for the opening balance.');
      return;
    }

    setBusy(true);
    try {
      if (isEdit && record?.id) {
        // Update — only the writable fields. Server's updateSchema
        // strips unknown keys, but we don't even send the locked ones.
        const payload = {
          accountName:        values.accountName,
          accountGroup:       values.accountGroup || null,
          accountSubtype:     values.accountSubtype || null,
          gstRelevant:        !!values.gstRelevant,
          isControlAccount:   !!values.isControlAccount,
          openingBalance:     ob,
          openingBalanceType: ob > 0 ? values.openingBalanceType : null,
          isActive:           values.isActive !== false
        };
        await accountsApi.updateLedger(record.id, payload);
        message.success('Ledger updated.');
      } else {
        const payload = {
          accountCode:        String(values.accountCode || '').trim(),
          accountName:        String(values.accountName || '').trim(),
          accountType:        values.accountType,
          accountGroup:       values.accountGroup || null,
          accountSubtype:     values.accountSubtype || null,
          normalBalance:      values.normalBalance,
          gstRelevant:        !!values.gstRelevant,
          isControlAccount:   !!values.isControlAccount,
          openingBalance:     ob,
          openingBalanceType: ob > 0 ? values.openingBalanceType : null,
          isActive:           values.isActive !== false
        };
        await accountsApi.createLedger(payload);
        message.success('Ledger created.');
      }
      onSaved?.();
    } catch (e) {
      message.error(unwrapApiError(e, isEdit
        ? 'Failed to update ledger.'
        : 'Failed to create ledger.'));
    } finally {
      setBusy(false);
    }
  }, [form, isEdit, readOnly, record, onClose, onSaved]);

  const title = useMemo(() => {
    if (isView)  return `Ledger Account — ${record?.accountCode || ''}`;
    if (isEdit) return `Edit Ledger — ${record?.accountCode || ''}`;
    return 'New Ledger Account';
  }, [isView, isEdit, record]);

  const isSystem = isEdit && record?.isSystem === true;

  return (
    <Modal
      open={open}
      title={title}
      onCancel={onClose}
      onOk={readOnly ? onClose : onSubmit}
      okText={readOnly ? 'Close' : isEdit ? 'Save' : 'Create'}
      okButtonProps={{ loading: busy }}
      cancelButtonProps={{ disabled: busy }}
      width={680}
      centered
      destroyOnClose
      maskClosable={false}
      className="erp-accounts-modal"
    >
      {isSystem ? (
        <Alert
          type="info"
          showIcon
          banner
          style={{ marginBottom: 12 }}
          message="System ledger"
          description={
            <span style={{ fontSize: 12 }}>
              This is a seeded system ledger (used by the posting engine).
              Code, type, and normal balance are immutable; you can still
              update the display name, group, opening balance, and
              activity flag.
            </span>
          }
        />
      ) : null}
      {isView ? (
        <Alert
          type="info"
          banner
          style={{ marginBottom: 12 }}
          message="Read-only view. Use Edit from the grid menu to change values."
        />
      ) : null}

      <Form form={form} layout="vertical" disabled={readOnly}
        className="erp-accounts-form">
        <Row gutter={12}>
          <Col span={8}>
            <Form.Item
              label="Account Code"
              name="accountCode"
              rules={[
                { required: true, message: 'Code is required' },
                { max: 40, message: 'Max 40 characters' }
              ]}
              tooltip="Short unique identifier — e.g. CASH, BANK-HDFC, 4000.">
              <Input maxLength={40} disabled={isEdit || readOnly}
                placeholder="e.g. BANK-HDFC" />
            </Form.Item>
          </Col>
          <Col span={16}>
            <Form.Item
              label="Account Name"
              name="accountName"
              rules={[
                { required: true, message: 'Name is required' },
                { min: 2,  message: 'At least 2 characters' },
                { max: 200, message: 'Max 200 characters' }
              ]}>
              <Input maxLength={200} placeholder="e.g. HDFC Bank — Current A/C 12345" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={12}>
          <Col span={8}>
            <Form.Item
              label="Account Type"
              name="accountType"
              rules={[{ required: true, message: 'Account type is required' }]}>
              <Select
                disabled={isEdit || readOnly}
                options={ACCOUNT_TYPES.map((t) => ({
                  value: t.value,
                  label: (
                    <span>
                      <Tag color={ACCOUNT_TYPE_COLORS[t.value]}
                        style={{ marginInlineEnd: 6 }}>
                        {t.label}
                      </Tag>
                      <Text type="secondary" style={{ fontSize: 11.5 }}>
                        {t.normal === 'debit' ? 'Debit-normal' : 'Credit-normal'}
                      </Text>
                    </span>
                  )
                }))}
                placeholder="Select…"
                onChange={onTypeChange}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="Normal Balance"
              name="normalBalance"
              rules={[{ required: true, message: 'Normal balance is required' }]}
              tooltip="Auto-set from Account Type. Override only if you know the accounting category needs the opposite side.">
              <Radio.Group disabled={isEdit || readOnly}
                buttonStyle="solid" size="small">
                <Radio.Button value="debit">Debit</Radio.Button>
                <Radio.Button value="credit">Credit</Radio.Button>
              </Radio.Group>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Group" name="accountGroup"
              tooltip="Optional category — used by Trial Balance grouping and the Balance Sheet sub-totals.">
              <Select
                allowClear
                showSearch
                placeholder={accountTypeWatch ? 'e.g. Bank Accounts' : 'Pick account type first'}
                disabled={!accountTypeWatch && !readOnly && !isEdit}
                options={groupOptions.map((g) => ({ value: g.value, label: g.label }))}
                mode="combobox"
                filterOption={(input, opt) =>
                  String(opt?.label || '').toLowerCase().includes(input.toLowerCase())}
                onChange={onGroupChange}
              />
            </Form.Item>
          </Col>
        </Row>

        {/* Stage 6B.9B — Account Subtype: optional, filtered by
            account_type. Auto-fills from Group when operator picks a
            canonical Group; auto-fills Group when operator picks a
            Subtype. NULL stays valid (legacy ledgers). */}
        <Row gutter={12}>
          <Col span={16}>
            <Form.Item label="Account Subtype" name="accountSubtype"
              tooltip="Optional production-grade classification. Helps Contra Voucher, Fixed Assets, Investments, and report grouping pick the right ledgers."
              extra="Account subtype improves reports, Contra Voucher, Fixed Assets, and Investments classification.">
              <Select
                allowClear
                showSearch
                disabled={!accountTypeWatch}
                placeholder={accountTypeWatch
                  ? `Pick a subtype (e.g. ${subtypeOptions[0]?.label || 'Bank Account'})`
                  : 'Pick account type first'}
                options={subtypeOptions.map((s) => ({
                  value: s.value,
                  label: (
                    <span>
                      {s.label}
                      <Text type="secondary" style={{ marginLeft: 8, fontSize: 11.5 }}>
                        {s.group}
                      </Text>
                    </span>
                  )
                }))}
                filterOption={(input, opt) => {
                  const lbl = String(opt?.value || '').toLowerCase();
                  return lbl.includes(String(input).toLowerCase());
                }}
                onChange={onSubtypeChange}
              />
            </Form.Item>
          </Col>
        </Row>

        <Divider style={{ margin: '8px 0 12px' }} plain>
          <Text type="secondary" style={{ fontSize: 12 }}>Opening Balance (optional)</Text>
        </Divider>

        <Row gutter={12}>
          <Col span={8}>
            <Form.Item label="Amount (₹)" name="openingBalance">
              <InputNumber
                min={0}
                precision={2}
                style={{ width: '100%' }}
                placeholder="0.00"
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Side" name="openingBalanceType"
              tooltip="Required when opening balance is greater than zero.">
              <Radio.Group buttonStyle="solid" size="small">
                <Radio.Button value="debit">Debit</Radio.Button>
                <Radio.Button value="credit">Credit</Radio.Button>
              </Radio.Group>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Active" name="isActive" valuePropName="checked">
              <Switch />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={12}>
          <Col span={12}>
            <Form.Item label="GST-relevant" name="gstRelevant" valuePropName="checked"
              tooltip="Tick if this ledger is consumed by GST reports (CGST/SGST/IGST input/output etc.).">
              <Switch />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Control Account" name="isControlAccount" valuePropName="checked"
              tooltip="A control account is the parent for many sub-ledgers (e.g. Sundry Debtors → per-customer ledger).">
              <Switch />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
}
